import { Link } from "react-router-dom";
import { Minus, Check, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { trackEvent } from "../lib/analytics";

const AVERAGE = [
  { title: "Relies on referrals", text: "Depends heavily on existing customers and word of mouth." },
  { title: "Posts occasionally", text: "Content is created mainly when there is an announcement, offer, or package update." },
  { title: "Runs ads occasionally", text: "Advertising often begins when bookings are needed instead of being managed as a consistent system." },
  { title: "Promotes only packages", text: "Marketing focuses mainly on prices, dates, hotels, and package details." },
  { title: "Waits for enquiries", text: "The agency depends on customers finding them and starting the conversation." },
  { title: "Uses WhatsApp mainly for replies", text: "WhatsApp is treated primarily as a communication channel rather than part of the customer journey." },
  { title: "Follows up inconsistently", text: "Potential customers may not receive timely or structured follow-up." },
  { title: "Competes mainly on price", text: "Price becomes the easiest thing for customers to compare." },
  { title: "Website gives information", text: "The website explains the agency but is not necessarily designed to convert visitors into enquiries." },
  { title: "Measures activity", text: "Success is often judged by likes, followers, or posts rather than meaningful business opportunities." },
];

const LEADERS = [
  { title: "Build multiple sources of enquiries", text: "They actively create opportunities instead of depending only on referrals." },
  { title: "Use a planned content system", text: "Their content consistently builds awareness, trust, education, and interest." },
  { title: "Run structured campaigns", text: "Advertising is planned around specific audiences, packages, offers, and objectives." },
  { title: "Market the complete value proposition", text: "They communicate packages, credibility, service, trust, and customer benefits." },
  { title: "Actively attract potential pilgrims", text: "They place their agency in front of people who may be considering Hajj or Umrah." },
  { title: "Use WhatsApp strategically", text: "WhatsApp becomes part of the journey from initial interest to sales conversation." },
  { title: "Follow up systematically", text: "Interested customers are handled through a structured follow-up process." },
  { title: "Compete through value and trust", text: "They give customers reasons to choose them beyond simply offering the lowest price." },
  { title: "Use the website to convert", text: "Their website is designed to turn attention into enquiries and conversations." },
  { title: "Measure what matters", text: "They focus on enquiries, lead quality, campaign performance, and opportunities for improvement." },
];

export const ComparisonSection = () => (
  <section className="bg-cream py-24 md:py-32 relative overflow-hidden" data-testid="comparison-section">
    <div className="absolute inset-0 geo-pattern opacity-[0.04]" />
    <div className="relative max-w-7xl mx-auto px-6">
      <Reveal className="max-w-3xl">
        <SectionTag className="mb-6">The Difference</SectionTag>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-charcoal leading-[1.05]">
          Average Agency vs. Market Leaders: What Really Sets Them Apart
        </h2>
        <p className="mt-6 text-base md:text-lg text-charcoal/65 leading-relaxed">
          Market leaders are not always winning because they have better Hajj
          or Umrah packages. They are winning because they have built stronger
          systems for getting discovered, earning trust, and turning interest
          into enquiries.
        </p>
        <p className="mt-4 text-base text-charcoal/55 leading-relaxed">
          A good package matters. But if potential pilgrims do not discover
          your agency, remember your agency, or receive the right follow-up,
          that package may never get the opportunity to compete.
        </p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-2 gap-6 items-stretch">
        <Reveal className="h-full">
          <div
            className="h-full bg-white border border-charcoal/10 p-8 md:p-10"
            data-testid="average-agency-card"
          >
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-charcoal/45">
              Average Agency
            </div>
            <div className="mt-8 space-y-6">
              {AVERAGE.map((item, i) => (
                <div key={item.title} className="flex items-start gap-4" data-testid={`comparison-average-${i + 1}`}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-charcoal/15">
                    <Minus size={12} strokeWidth={2} className="text-charcoal/40" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-charcoal/80">{item.title}</div>
                    <div className="mt-1 text-sm text-charcoal/50 leading-relaxed">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="h-full">
          <div
            className="h-full bg-charcoal border border-gold/50 p-8 md:p-10 shadow-[0_24px_60px_rgba(45,42,39,0.25)]"
            data-testid="market-leaders-card"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">
                Market Leaders
              </div>
              <span className="h-2 w-2 rotate-45 bg-gold" />
            </div>
            <div className="mt-8 space-y-6">
              {LEADERS.map((item, i) => (
                <div key={item.title} className="flex items-start gap-4" data-testid={`comparison-leader-${i + 1}`}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-gold/50 bg-gold/10">
                    <Check size={12} strokeWidth={2.5} className="text-gold" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-cream">{item.title}</div>
                    <div className="mt-1 text-sm text-cream/55 leading-relaxed">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div
          className="mt-14 bg-tan px-8 py-10 md:px-14 md:py-14 relative overflow-hidden"
          data-testid="comparison-key-message"
        >
          <div className="absolute inset-0 geo-pattern-light opacity-[0.08]" />
          <div className="relative max-w-4xl">
            <p className="text-2xl md:text-4xl font-bold tracking-tight text-cream leading-[1.15]">
              You do not need to become the biggest agency in the market. You
              need to stop marketing like an average one.
            </p>
            <p className="mt-5 text-sm md:text-base text-cream/80 leading-relaxed max-w-2xl">
              A smaller agency can still compete for attention when it has the
              right positioning, content, advertising, customer journey, and
              follow-up system in place.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          className="mt-6 bg-charcoal px-8 py-12 md:px-14 md:py-16 relative overflow-hidden"
          data-testid="urgency-cta-block"
        >
          <div className="absolute inset-0 geo-pattern-light opacity-[0.05]" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-10 justify-between">
            <div className="max-w-2xl">
              <h3 className="text-2xl md:text-4xl font-bold tracking-tight text-cream leading-[1.1]">
                Don't Let Market Leaders Take Your Next Pilgrims.
              </h3>
              <p className="mt-5 text-sm md:text-base text-cream/60 leading-relaxed">
                Every Hajj and Umrah season, agencies compete for the same
                potential pilgrims. The agencies that are visible, trusted, and
                ready to follow up give themselves a stronger chance of being
                chosen first.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="urgency-contact-button"
              onClick={() => trackEvent("cta_click", { cta: "contact_us", placement: "comparison_urgency" })}
              className="group inline-flex items-center gap-2 bg-tan text-cream text-sm font-semibold tracking-wide px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-charcoal shrink-0"
            >
              Contact Us
              <ArrowUpRight
                size={16}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
